import { defineMessages, useIntl } from 'react-intl'
import { Flex, Heading, LoadingSpinner, Stack, Text } from '@homebotapp/hb-react-component-catalog'
import { Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import Section from '../Section/Section'
import { useState } from 'react'
import { ShortTermRentalCardCollapsed } from './ShortTermRentalCardCollapsed'
import { ShortTermRentalCardExpanded } from './ShortTermRentalCardExpanded'
import { execute } from '../../../../vendor/recaptcha'
import * as Sentry from '@sentry/browser'
import { fetchAirDna } from '../../../../api/mikasa/requests'
import { fetchAirDna as fetchAirDnaWithHeaders } from '../../../../api/mikasa/token_requests'
import { useSelector } from 'react-redux'
import { selectClientIsGuest } from '../../../../store/selectors/client'
import { selectCustomerPublicKey } from '../../../../store/selectors/customerProfile'

interface ShortTermRentalProps {
  listing: Listing
}

export const MSG = defineMessages({
  error: {
    id: 'ShortTermRental.error',
    defaultMessage:
      "Sorry, we don't have short term rental information on this property right now. Please check back soon."
  },
  loading: {
    id: 'ShortTermRental.loading',
    defaultMessage: 'Getting the latest data...'
  }
})

export const ShortTermRental: React.FC<ShortTermRentalProps> = ({ listing }) => {
  const intl = useIntl()

  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<any>(null)
  const [error, setError] = useState<Error | null>(null)

  const isGuest = useSelector(selectClientIsGuest)
  const customerPublicKey = useSelector(selectCustomerPublicKey)

  const handleExpand = async () => {
    if (!data && !loading && !error) {
      setLoading(true)
      setError(null)

      const address = `${listing.address?.number || ''} ${listing.address?.street || ''} ${
        listing.address?.city || ''
      }, ${listing.address?.stateOrProvince || ''} ${listing.address?.postalCode || ''}`.trim()

      try {
        const validationToken = await execute({ action: 'request_rental_potential' })

        if (validationToken) {
          let response

          if (isGuest && customerPublicKey) {
            // fetch with hb-cust-pub header for guest mode
            response = await fetchAirDnaWithHeaders({
              address,
              validationToken
            })
          } else {
            // fetch with token for authenticated users
            response = await fetchAirDna({
              address,
              validationToken
            })
          }

          if (response.data) {
            setData({
              data: response.data.payload,
              settings: {
                pricePerNight: Math.round(response.data.payload.stats.future.summary.adr * 0.7),
                nightsPerMonth: Math.round(response.data.payload.stats.future.summary.occupancy * 30 * 0.7)
              }
            })
            setLoading(false)
            setIsExpanded(true)
          } else {
            setError(new Error('No data returned from AirDNA'))
            Sentry.captureException(new Error('No data returned from AirDNA'))
            setLoading(false)
          }
        }
      } catch (e) {
        setError(e)
        Sentry.captureException(e)
        setLoading(false)
      }
    }
  }

  if (error) {
    return (
      <Section>
        <Section.Body>
          <Flex justify='center' align='center' my={8}>
            <Heading>{intl.formatMessage(MSG.error)}</Heading>
          </Flex>
        </Section.Body>
      </Section>
    )
  }

  return (
    <Section>
      <Section.Body>
        {loading ? (
          <Stack justify='center' align='center' my={8}>
            <Text>{intl.formatMessage(MSG.loading)}</Text>
            <LoadingSpinner />
          </Stack>
        ) : isExpanded ? (
          <ShortTermRentalCardExpanded listing={listing} data={data} />
        ) : (
          <ShortTermRentalCardCollapsed setIsExpanded={handleExpand} listingId={listing.id} />
        )}
      </Section.Body>
    </Section>
  )
}
